import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGetRole } from 'hooks/ui/useGetRole';
import { Grid } from '@mui/material';
import { IconButton, styled, Tooltip, Switch } from '@mui/material';
import {
  Delete,
  Edit,
  Visibility,
  Email,
  InfoOutlined,
  ContactMail,
  OpenInNew,
} from '@mui/icons-material';
import Autocomplete from 'components/FormElements/AppAutocomplete';
import TextField from 'components/FormElements/AppTextInput';
import GridContainer from 'components/GridContainer';
import Table, { TableActions } from 'components/Table';
import PageContainer from 'components/PageContainer';
import {
  INVESTOR_ADD,
  createAdviserViewRoute,
  createInvestorEditRoute,
  createInvestorViewRoute,
} from 'adminConstants/routes';
import AlertDialog from 'components/AlertDialog';
import { dateToLabel, dateofBirthToLabel } from 'further-ui/utils';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { useSortTranches } from 'hooks/ui/useSortTranches';
import { useFunds } from 'hooks/data/fund/useFunds';
import usePlatformInvites from 'hooks/data/investor/useInvestorPlatformInvites';
import { useLaunchPortalImpersonation } from 'hooks/data/impersonationAccess/useLaunchPortalImpersonation';
import { useInvestorDisability } from 'hooks/data/investor/useInvestorDisability';
import PageContent from 'components/PageContent';
import PageFilters, { Filter } from 'components/PageFilters';
import { FirmSelector } from 'components/FirmSelector';
import SearchField from 'components/SearchField';
import api, { ApiResponses } from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useBackgroundExports } from 'hooks/data/backgroundExports/useBackgroundExports';
import { BackgroundExportTypes } from 'further-types/background-exports';
import { InvestorLink } from 'components/Links';
import { getQueryKey } from '@trpc/react-query';

const ResendInvites = styled('div')({
  fontSize: '2rem',
});

const StyledAdviserLink = styled(Link)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.text.green,
}));

type InvestorRow = ApiResponses['investor']['listInvestors']['result'][0];

type InvestorColumn = {
  label: string | ReactNode;
  key: string;
  render?: (row: InvestorRow) => ReactNode;
  sort?: boolean;
};

const Investor: React.FC = () => {
  const deleteDisclosure = useDisclosure();
  const disabledDisclosure = useDisclosure();
  const resendInvitesDisclosure = useDisclosure();
  const activeInviteDisclosure = useDisclosure();
  const { isSuperAdmin, firmId, firmName } = useGetRole();
  const [selectedInvestors, setSelectedInvestors] = useState<
    Array<InvestorRow>
  >([]);

  const pagination = usePagination({
    pageSize: 10,
    orderBy: 'createdAt',
    order: 'desc',
    id: 'investor-list',
  });
  const { filters, handleChangeFilters } = useFilters('investor-filter', {
    defaultFilters: {
      firmId,
      fundId: '',
      searchTerm: '',
    },
    onFiltersChange: pagination.toFirstPage,
  });

  const resendInvites = api.investor.resendPlatformInvites.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Investor invite sent successfully.',
    }),
  );
  const { resendInvitesToInvestorsWithoutVisit } = usePlatformInvites();

  const [
    updatePermission,
    deletePermission,
    createPermission,
    impersonationPermission,
  ] = useHasPermissions([
    RolePermission.EditInvestor,
    RolePermission.DeleteInvestor,
    RolePermission.CreateInvestor,
    RolePermission.UseImpersonationAccessInvestor,
  ]);

  const { tranches } = useFunds({
    firmId: filters.firmId,
    leanResponse: true,
  });
  const sortedTranches = useSortTranches(tranches?.data);

  const urlParams = new URLSearchParams(window.location.search);
  const fundIdParam = urlParams.get('fundId');

  const [filterSetFromSearchParams, setFilterSetFromSearchParams] =
    useState(false);

  useEffect(() => {
    if (!tranches?.isFetching && tranches?.data && !filterSetFromSearchParams) {
      const foundFund = tranches.data.find(
        (tranche) => tranche._id === fundIdParam,
      );
      if (foundFund) {
        handleChangeFilters({ fundId: foundFund?._id });
        setFilterSetFromSearchParams(true);
      }
    }
  }, [
    tranches?.data,
    filters.fundId,
    tranches?.isFetching,
    filterSetFromSearchParams,
  ]);

  const { data, isFetching } = api.investor.listInvestors.useQuery({
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    order: pagination.order,
    orderBy: pagination.orderBy,
    fundId: filters.fundId,
    search: filters.searchTerm,
    firmId: filters.firmId,
  });

  const { mutate: updateInvestorDisability } = useInvestorDisability();
  const { mutate: deleteInvestor, isPending: isDeleting } =
    api.investor.deleteInvestor.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Investor deleted successfully.',
        invalidations: [getQueryKey(api.investor.listInvestors)],
        onSuccess: () => {
          deleteDisclosure.onClose();
          activeInviteDisclosure.onClose();
        },
      }),
    );
  const utils = api.useUtils();
  const launchPortalImpersonation = useLaunchPortalImpersonation();

  const columns: Array<InvestorColumn> = useMemo(
    () => [
      {
        label: 'Investor name',
        key: 'fullName',
        render: (elm) => (
          <InvestorLink investorId={elm?._id} investorName={elm?.fullName} />
        ),
      },
      ...(isSuperAdmin ? [{ label: 'Firm', key: 'firmName' }] : []),
      { label: 'Email', key: 'email' },
      {
        label: 'Birth/incorporation',
        key: 'birthOrIncorporation',
        render: (elm) => dateofBirthToLabel(elm.birthOrIncorporation),
      },
      {
        label: 'KYC',
        key: 'kycStatusLabel',
      },
      {
        label: 'Created date',
        key: 'createdAt',
        render: (elm) => dateToLabel(elm?.createdAt),
      },
      {
        label: 'System emails',
        key: 'disableAllEmails',
        render: (elm) => (elm?.disableAllEmails ? 'Disabled' : 'Enabled'),
      },
      {
        label: 'Last login date',
        key: 'lastLoginDate',
        render: (elm) => dateToLabel(elm?.lastLoginDate),
      },
      {
        label: 'Actions',
        key: 'actions',
        sort: false,
        render: (elm) => (
          <TableActions
            showAsDropdown
            actions={[
              {
                link: createInvestorViewRoute(elm._id),
                label: 'View',
                color: 'primary',
                icon: Visibility,
              },
              {
                visible: updatePermission,
                label: 'Edit',
                icon: Edit,
                color: 'primary',
                disabled: false,
                link: createInvestorEditRoute(elm._id),
              },
              {
                visible: deletePermission,
                label: 'Delete',
                icon: Delete,
                color: 'error',
                onClick: async () => {
                  try {
                    const result =
                      await utils.investor.getActiveAdviserInvitation.fetch({
                        investorId: elm._id,
                      });
                    if (result?.hasActive) {
                      activeInviteDisclosure.stageAction({
                        name: result.adviserFullName ?? '',
                        id: result.adviserId ?? '',
                        investorId: elm._id,
                      });
                    } else {
                      deleteDisclosure.stageAction({ id: elm._id });
                    }
                  } catch {
                    // if the pre-check fails for any reason, fall back to normal delete
                    deleteDisclosure.stageAction({ id: elm._id });
                  }
                },
                disabled: elm?.investmentCount > 0,
                tooltip:
                  elm?.investmentCount > 0
                    ? 'When an investor has made a subscription, financial regulation prohibits account deletion.'
                    : undefined,
              },
              {
                icon: Email,
                label: 'Resend invite',
                onClick: () => resendInvites.mutate({ investorIds: [elm._id] }),
                color: 'primary',
                tooltip:
                  'This resends the investor invite and enables future emails to this investor.',
              },
              {
                icon: OpenInNew,
                visible: impersonationPermission,
                label: 'View investor portal',
                onClick: () =>
                  launchPortalImpersonation.investor({
                    investorId: elm._id,
                    firmDomain: elm.firmDomain,
                  }),
                color: 'primary',
                disabled: !elm.firmDomain,
              },
            ]}
          />
        ),
      },
      {
        label: (
          <span>
            Active?
            <Tooltip title="When an investor has made a subscription, financial regulation prohibits account deletion. If an active user no longer wishes to use the Further platform, switch the green toggle off to deactivate the user.">
              <IconButton size="small">
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </span>
        ),
        sort: false,
        key: 'active',
        render: (elm) =>
          elm?.investmentCount ? (
            <Tooltip
              title={
                elm?.isDisabled
                  ? 'Click to activate the user. Activated users are able to log in and view their portal.'
                  : 'Click to deactivate the user. Deactivated users are unable to log in and cannot view their portal.'
              }
            >
              <Switch
                inputProps={{ 'aria-label': 'Switch' }}
                checked={!elm?.isDisabled}
                onChange={(event) => handleSwitchInvestorDisability(event, elm)}
                disabled={!updatePermission}
              />
            </Tooltip>
          ) : (
            ''
          ),
      },
    ],
    [updatePermission, deletePermission, deleteDisclosure, isSuperAdmin],
  );

  const breadcrumbs = [
    { label: 'Dashboard', link: '/' },
    { label: 'Investors', isActive: true },
  ];

  const handleSwitchInvestorDisability = (event, rec) => {
    if (!event.target.checked) {
      disabledDisclosure.stageAction({ id: rec._id });
    } else {
      updateInvestorDisability({
        investorId: rec._id,
        isDisabled: false,
      });
    }
  };

  const confirmInvestorDisability = () => {
    updateInvestorDisability({
      investorId: disabledDisclosure.actionProps.id,
      isDisabled: true,
    });
    disabledDisclosure.onClose();
  };

  const handleConfirmDelete = () => {
    const investorId = deleteDisclosure.actionProps.id;
    deleteInvestor({ investorId });
  };

  const { createBackgroundExport, isExporting } = useBackgroundExports();

  const handleExport = () => {
    createBackgroundExport.mutate({
      exportType: BackgroundExportTypes.InvestorExport,
      params: {
        fundId: filters.fundId || undefined,
        search: filters.searchTerm,
        sortType: pagination.order,
        keyName: pagination.orderBy,
      },
      firmId: filters.firmId || undefined,
    });
  };

  const fundOptions = [
    { label: 'No subscriptions', _id: 'no-investment' },
  ].concat(sortedTranches);

  return (
    <PageContainer heading="Investors" breadcrumbs={breadcrumbs}>
      <PageContent>
        <PageFilters
          pageId="investor-list"
          buttons={[
            {
              visible: !isSuperAdmin,
              onClick: resendInvitesDisclosure.onOpen,
              loading: resendInvitesToInvestorsWithoutVisit.isPending,
              label: (
                <Tooltip title="Click to re-invite all active investors who have not previously logged in (excludes disabled accounts and investors without subscriptions).">
                  <ResendInvites>
                    <ContactMail />
                  </ResendInvites>
                </Tooltip>
              ),
            },
            {
              disabled: isFetching,
              loading: isExporting,
              onClick: handleExport,
              label: 'Export',
            },
            {
              link: INVESTOR_ADD,
              label: 'Add investor',
              visible: createPermission,
            },
            {
              label: 'Resend invites for selected investors',
              visible: !!selectedInvestors.length,
              loading: resendInvites.isPending,
              onClick: () =>
                resendInvites.mutate({
                  investorIds: selectedInvestors.map(
                    (investor) => investor._id,
                  ),
                }),
            },
          ]}
        >
          {isSuperAdmin && (
            <Filter>
              <FirmSelector
                firmId={filters.firmId}
                placeholder="Search by firm"
                onChange={(firmId) => {
                  handleChangeFilters({ firmId });
                }}
              />
            </Filter>
          )}
          <Filter>
            <SearchField
              name="investorName"
              placeholder="Search by name or email"
              fullWidth
              allowEmptyQuery
              autoSearchOnDebounce
              value={filters.searchTerm}
              onSearch={(searchTerm) => handleChangeFilters({ searchTerm })}
            />
          </Filter>
          <Filter>
            <Autocomplete
              key={sortedTranches?.length}
              id="fundId"
              options={fundOptions}
              getOptionLabel={(option) => option.label}
              getOptionKey={(option) => option._id}
              value={
                fundOptions?.find((fund) => fund._id === filters.fundId) ?? null
              }
              filterSelectedOptions
              onChange={(_, newValue) => {
                handleChangeFilters({ fundId: newValue?._id });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Tranche"
                />
              )}
            />
          </Filter>
        </PageFilters>
        <GridContainer>
          <Grid size={12}>
            <Table
              columns={columns}
              onRequestSort={pagination.handleRequestSort}
              order={pagination.order}
              orderBy={pagination.orderBy}
              tablebody={data?.result ?? []}
              onPageChange={pagination.handleChangePage}
              onRowsPerPageChange={pagination.handleChangeRowsPerPage}
              page={pagination.page}
              rowsPerPage={pagination.rowsPerPage}
              count={data?.total || 0}
              pagination={true}
              loading={isFetching}
              variant="nohover"
              selectable
              onSelectionChange={setSelectedInvestors}
            />
          </Grid>
        </GridContainer>
      </PageContent>
      <AlertDialog
        open={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        onConfirm={handleConfirmDelete}
        title={'Are you sure you wish to delete this investor?'}
        content={
          <p>
            This cannot be undone and all information relating to the investor
            will be lost.
          </p>
        }
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Delete investor',
        }}
        confirmBtnProps={{
          disabled: isDeleting,
          loading: isDeleting,
        }}
      />
      <AlertDialog
        open={activeInviteDisclosure.isOpen}
        onClose={activeInviteDisclosure.onClose}
        onConfirm={() => {
          const investorId = activeInviteDisclosure.actionProps?.investorId;
          if (investorId) {
            deleteInvestor({
              investorId,
              deleteActiveInvitations: true,
            });
          }
        }}
        title="This user has an active invitation"
        content={
          <p>
            This user has been invited to make an investment by an adviser{' '}
            <StyledAdviserLink
              to={createAdviserViewRoute(
                activeInviteDisclosure.actionProps?.id,
              )}
              target="_blank"
            >
              {activeInviteDisclosure.actionProps?.name}
            </StyledAdviserLink>
            . If you proceed this will also delete the invitation.
          </p>
        }
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Delete investor and invitation',
        }}
        confirmBtnProps={{
          disabled: isDeleting,
          loading: isDeleting,
        }}
      />
      <AlertDialog
        open={disabledDisclosure.isOpen}
        onClose={disabledDisclosure.onClose}
        onConfirm={confirmInvestorDisability}
        title={'Are you sure you wish to deactivate this investor?'}
        content={
          <p>
            Deactivating this user will prevent them from being able to log in
            to the platform, make subscriptions or access their investor portal.
          </p>
        }
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Deactivate investor',
        }}
      />
      <AlertDialog
        open={resendInvitesDisclosure.isOpen}
        onClose={resendInvitesDisclosure.onClose}
        onConfirm={() => {
          resendInvitesToInvestorsWithoutVisit.mutate();
          resendInvitesDisclosure.onClose();
        }}
        title="Are you sure?"
        content={
          <p>
            This will resend email invites to all investors that have not yet
            logged in to the {firmName ?? ''} portal and will activate system
            emails for these users. Re-sending invites cannot be undone.
          </p>
        }
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Proceed',
        }}
      />
    </PageContainer>
  );
};
export default Investor;
